/** Build your own report.
 *
 *  The Reports screen answers questions somebody chose in advance — completion rate,
 *  turnaround, who is carrying what. This answers the ones they did not: pick the
 *  filters, pick how it is grouped, read the rows, take the file away.
 *
 *  **The export is the same request as the screen**, with a different extension, so a
 *  spreadsheet can never disagree with the page it came from. One filter function on
 *  the server feeds all three.
 */
import { Fragment, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, download } from '@/api/client'
import { Chips, Empty, ErrorNote, Spinner, toggleIn } from '@/components/ui'

type Options = {
  groupings: { value: string; label: string }[]
  statuses: string[]
  priorities: string[]
  companies: { id: string; name: string }[]
  people: { id: string; name: string }[]
  columns: string[]
}
type Records = {
  total: number
  group_by: string
  columns: string[]
  groups: { key: string; count: number; rows: (string | number)[][] }[]
}

const BLANK = {
  date_from: '', date_to: '', company_id: '', responsible_id: '', approver_id: '',
  carried_forward: '', q: '', group_by: 'none',
}

// `status` and `priority` are deliberately absent from `BLANK`: they are lists, read
// with `getAll` and written by repeating the parameter, which is the shape FastAPI
// reads a `List[str]` from. The API has always taken lists for these two and returned
// the union - a single `<select>` was the only thing insisting on one at a time.

export function Explore() {
  // The filters live in the URL, the same rule All tasks states in its own docstring:
  // so a refresh does not lose them, so a filtered view can be sent to somebody, and
  // - the reason it changed here - so All tasks can hand its filters straight over
  // rather than making somebody set them all again on arrival.
  const [params, setParams] = useSearchParams()
  const f = { ...BLANK } as typeof BLANK
  for (const k of Object.keys(BLANK) as (keyof typeof BLANK)[]) {
    const v = params.get(k)
    if (v !== null) f[k] = v
  }
  const multi = {
    status: params.getAll('status').filter(Boolean),
    priority: params.getAll('priority').filter(Boolean),
  }

  const write = (patch: Record<string, string | string[] | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      next.delete(k)
      if (Array.isArray(v)) for (const one of v) next.append(k, one)
      else if (v !== null && v !== '') next.set(k, v)
    }
    setParams(next, { replace: true })
  }

  const set = (k: keyof typeof BLANK, v: string) => write({ [k]: v })
  const toggle = (k: 'status' | 'priority', v: string) =>
    write({ [k]: toggleIn(multi[k], v) })
  const clear = () => setParams(new URLSearchParams(), { replace: true })

  const [saving, setSaving] = useState('')
  const [saveErr, setSaveErr] = useState<unknown>(null)

  const options = useQuery({
    queryKey: ['report-options'],
    queryFn: () => api.get<Options>('/reports/options'),
  })

  // One query string for the screen and both downloads — that is what keeps them
  // honest with each other.
  const qs = () => {
    const p = new URLSearchParams()
    for (const [k, v] of Object.entries(f)) if (v) p.set(k, v)
    // Repeated rather than comma-joined: that is how FastAPI reads a `List[str]`,
    // and it is what the screen and both downloads all send.
    for (const s of multi.status) p.append('status', s)
    for (const pr of multi.priority) p.append('priority', pr)
    return p.toString()
  }
  const query = qs()

  const data = useQuery({
    queryKey: ['report-records', query],
    queryFn: () => api.get<Records>(`/reports/records?${query}`),
  })

  const save = async (ext: 'xlsx' | 'csv') => {
    setSaveErr(null)
    setSaving(ext)
    try {
      await download(`/reports/records.${ext}?${qs()}`)
    } catch (e) {
      setSaveErr(e)
    } finally {
      setSaving('')
    }
  }

  if (options.isLoading) return <Spinner label="Loading the filters" />
  if (options.error) return <ErrorNote error={options.error} />
  const o = options.data!

  const dirty = Object.entries(f).some(([k, v]) => v !== BLANK[k as keyof typeof BLANK])
    || multi.status.length > 0 || multi.priority.length > 0
  const grouped = f.group_by !== 'none'
  const groupLabel = o.groupings.find((g) => g.value === f.group_by)?.label ?? ''

  const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="min-w-0">
      <label className="label">{label}</label>
      {children}
    </div>
  )

  return (
    <>
      <div className="card mb-4">
        <div className="card-head">
          <b className="text-[13px]">What do you want to see?</b>
          <span className="flex-1" />
          {dirty && (
            <button className="btn btn-sm" onClick={clear}>
              Clear all
            </button>
          )}
        </div>

        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="From">
            <input type="date" className="field" value={f.date_from}
                   onChange={(e) => set('date_from', e.target.value)} />
          </Field>
          <Field label="To">
            <input type="date" className="field" value={f.date_to}
                   onChange={(e) => set('date_to', e.target.value)} />
          </Field>
          <Field label="Company">
            <select className="field" value={f.company_id}
                    onChange={(e) => set('company_id', e.target.value)}>
              <option value="">Any company</option>
              {o.companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </Field>
          <Chips label="Priority" anyLabel="Any priority"
                 selected={multi.priority}
                 onToggle={(v) => toggle('priority', v)}
                 options={o.priorities.map((p) => ({ value: p, label: p }))} />
          <Field label="Responsible">
            <select className="field" value={f.responsible_id}
                    onChange={(e) => set('responsible_id', e.target.value)}>
              <option value="">Anyone</option>
              {o.people.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
          </Field>
          <Field label="Approver">
            <select className="field" value={f.approver_id}
                    onChange={(e) => set('approver_id', e.target.value)}>
              <option value="">Anyone</option>
              {o.people.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
          </Field>
          <div className="sm:col-span-2">
            <Chips label="Status" anyLabel="Any status"
                   selected={multi.status}
                   onToggle={(v) => toggle('status', v)}
                   options={o.statuses.map((s) => (
                     { value: s, label: s.replace('_', ' ').toLowerCase() }))} />
          </div>
          <Field label="Carried forward">
            <select className="field" value={f.carried_forward}
                    onChange={(e) => set('carried_forward', e.target.value)}>
              <option value="">Either</option>
              <option value="true">Has been carried</option>
              <option value="false">Never carried</option>
            </select>
          </Field>
          <div className="sm:col-span-2">
            <label className="label">Search</label>
            <input className="field" value={f.q}
                   placeholder="Task, company, person or remark"
                   onChange={(e) => set('q', e.target.value)} />
          </div>
          <Field label="Group by">
            <select className="field" value={f.group_by}
                    onChange={(e) => set('group_by', e.target.value)}>
              {o.groupings.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>))}
            </select>
          </Field>
          <div className="flex items-end gap-2">
            <button className="btn flex-1" disabled={!!saving || !data.data?.total}
                    onClick={() => save('xlsx')}>
              {saving === 'xlsx' ? 'Preparing…' : 'Excel'}
            </button>
            <button className="btn flex-1" disabled={!!saving || !data.data?.total}
                    onClick={() => save('csv')}>
              {saving === 'csv' ? 'Preparing…' : 'CSV'}
            </button>
          </div>
        </div>
        <div className="px-4 py-2.5 border-t border-rule text-[11.8px] text-ink-3">
          The download is this same list — the filters and grouping above go with it,
          so the file and the screen always agree.
        </div>
      </div>

      {saveErr ? <div className="mb-4"><ErrorNote error={saveErr} /></div> : null}

      {data.isLoading ? <Spinner />
        : data.error ? <ErrorNote error={data.error} />
        : !data.data?.total ? (
          <Empty title="Nothing matches"
                 hint={dirty
                   ? 'Try removing a filter — the date range is the usual culprit.'
                   : 'There are no tasks yet.'}
                 action={dirty
                   ? <button className="btn btn-primary"
                             onClick={clear}>Clear all</button>
                   : undefined} />
        ) : (
          <div className="card">
            <div className="card-head">
              <b className="text-[13px]">
                {data.data.total} row{data.data.total === 1 ? '' : 's'}
              </b>
              {grouped && (
                <span className="text-[11.8px] text-ink-3">
                  in {data.data.groups.length} group
                  {data.data.groups.length === 1 ? '' : 's'} by {groupLabel.toLowerCase()}
                </span>
              )}
            </div>
            <div className="table-wrap">
              <table className="w-full border-collapse min-w-[1100px] lg:min-w-0">
                <thead>
                  <tr>
                    {data.data.columns.map((h) => <th key={h} className="th">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.data.groups.map((g) => (
                    <Fragment key={g.key}>
                      {grouped && (
                        <tr>
                          <td colSpan={data.data!.columns.length}
                              className="bg-primary/[0.07] border-y border-rule
                                         px-2.5 py-2">
                            <span className="text-[13px] font-semibold tracking-tight">
                              {g.key || '(none)'}
                            </span>
                            <span className="mono text-[11.5px] text-ink-3 ml-2">
                              {g.count} row{g.count === 1 ? '' : 's'}
                            </span>
                          </td>
                        </tr>
                      )}
                      {g.rows.map((row, i) => (
                        <tr key={`${g.key}-${i}`}>
                          {row.map((cell, j) => (
                            <td key={j}
                                className={`td text-[12.5px] ${
                                  j === 0 ? 'mono text-primary font-medium' : ''}`}>
                              {String(cell ?? '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </>
  )
}
